import { Link } from 'react-router-dom';
import { useCategories, useAllProducts } from '@/hooks/useProducts';
import ProductCard from '@/components/ProductCard';
import SocialIcon from '@/components/SocialIcon';
import { Truck, Shield, Award } from 'lucide-react';
import { useMemo, lazy, Suspense } from 'react';
import SEOHead, { organizationSchema, websiteSchema, faqSchema, buildHreflangPaths } from '@/components/SEOHead';
import { useLanguage } from '@/hooks/useLanguage';
import { useTranslations } from '@/hooks/useTranslations';
import { useSeoText } from '@/hooks/useSeoText';
import { useCategoryTranslations, useProductTranslations, applyProductTranslation, applyCategoryTranslation } from '@/hooks/useContentTranslation';
import { useStorefront } from '@/hooks/useStorefront';
import { AT_SEO_DEFAULTS } from '@/lib/atSeoDefaults';
import { IT_SEO_DEFAULTS } from '@/lib/itSeoDefaults';
import { AL_SEO_DEFAULTS } from '@/lib/alSeoDefaults';

// Lazy-load der dekorativen Hero-Notifications-Karte (nur md+ sichtbar).
// Das hält framer-motion (~129 KB) komplett aus dem Critical Path / LCP raus.
const HeroNotifications = lazy(() =>
  import('@/components/HeroNotifications').catch((err) => {
    // Stale chunk after redeploy: reload once to fetch new hashed asset.
    if (!sessionStorage.getItem('bf_chunk_reloaded_hero')) {
      sessionStorage.setItem('bf_chunk_reloaded_hero', '1');
      window.location.reload();
    }
    throw err;
  })
);

export default function HomePage() {
  const { categories: rawCategories, loading: catLoading } = useCategories();
  const { products: rawProducts, loading: prodLoading } = useAllProducts();
  const { localePath } = useLanguage();
  const { t } = useTranslations();
  const { storefront } = useStorefront();
  const isAt = storefront === 'at';
  const isIt = storefront === 'it';
  const isAl = storefront === 'al';

  const categoryIds = useMemo(() => rawCategories.map(c => c.id), [rawCategories]);
  const categoryOverlays = useCategoryTranslations(categoryIds);
  const categories = useMemo(() =>
    rawCategories.map(c => applyCategoryTranslation(c, categoryOverlays[c.id] || null)),
    [rawCategories, categoryOverlays]
  );

  const productIds = useMemo(() => rawProducts.map(p => p.id), [rawProducts]);
  const productOverlays = useProductTranslations(productIds);
  const products = useMemo(() =>
    rawProducts.map(p => applyProductTranslation(p, productOverlays[p.id] || null)),
    [rawProducts, productOverlays]
  );

  const loading = catLoading || prodLoading;

  // Per-storefront SEO overrides for hero copy. CH-FASTPATH returns base
  // strings immediately with zero DB reads and zero react-query subscription.
  const heroTitleBase = isAl
    ? AL_SEO_DEFAULTS.heroTitle
    : isIt
    ? IT_SEO_DEFAULTS.heroTitle
    : isAt
      ? AT_SEO_DEFAULTS.heroTitle
      : t('home.hero_title', 'Follower kaufen mit TWINT');
  const heroSubtitleBase = isAl
    ? AL_SEO_DEFAULTS.heroSubtitle
    : isIt
    ? IT_SEO_DEFAULTS.heroSubtitle
    : isAt
      ? AT_SEO_DEFAULTS.heroSubtitle
      : t('home.hero_subtitle', 'Instagram & TikTok schnell steigern');
  const heroTextBase = isAl
    ? AL_SEO_DEFAULTS.heroText
    : isIt
    ? IT_SEO_DEFAULTS.heroText
    : isAt
      ? AT_SEO_DEFAULTS.heroText
      : t('home.hero_text', 'Hochwertige, echte Follower sowie Likes, Views und Kommentare – in Minuten geliefert, bezahlt mit TWINT, Visa oder Mastercard, so einfach wie günstig – die besten Preise der Schweiz.');
  const heroTitle = useSeoText(heroTitleBase, 'route', '/', 'h1');
  const heroSubtitle = useSeoText(heroSubtitleBase, 'route', '/', 'hero_subtitle');
  const heroText = useSeoText(heroTextBase, 'route', '/', 'hero_text');

  return (
    <>
      <SEOHead
        title={isAl
          ? AL_SEO_DEFAULTS.title
          : isIt
          ? IT_SEO_DEFAULTS.title
          : isAt
            ? AT_SEO_DEFAULTS.title
            : t('home.seo_title', 'Follower kaufen mit TWINT – Instagram & TikTok')}
        description={isAl
          ? AL_SEO_DEFAULTS.description
          : isIt
          ? IT_SEO_DEFAULTS.description
          : isAt
            ? AT_SEO_DEFAULTS.description
            : t('home.seo_description', 'Follower kaufen mit TWINT – Instagram & TikTok Follower, Likes, Views günstig kaufen. Beste Preise der Schweiz. Sofortige Lieferung.')}
        canonical={`${isIt ? 'https://bigfollow.it' : isAt ? 'https://bigfollow.at' : 'https://bigfollow.ch'}${localePath('/')}`}
        hreflangPaths={buildHreflangPaths('/')}
        jsonLd={[
          organizationSchema(isAl
            ? { siteUrl: 'https://bigfollow.al', description: AL_SEO_DEFAULTS.description, addressCountry: 'AL' }
            : undefined),
          websiteSchema(isAl ? { siteUrl: 'https://bigfollow.al' } : undefined),
          faqSchema([
            { question: t('home.faq1_q', 'Warum sollte ich Followers kaufen?'), answer: t('home.faq1_a', 'Je mehr Follower du hast, desto mehr Aufmerksamkeit wirst du von anderen Nutzern oder Unternehmen bekommen. Gute Inhalte sind der Schlüssel, um die Followerzahlen zu steigern.') },
            { question: t('home.faq2_q', 'Sind die Followers echt?'), answer: t('home.faq2_a', 'Ja, wir liefern ausschliesslich echte Follower von echten und aktiven Accounts. Alle diese Nutzer haben Profilbilder, Posts, Follower und folgen auch anderen Accounts.') },
          ]),
        ]}
      />
      {/* Hero – min-height reserves vertical space so font swap (Inter)
          and translation hydration don't cause a layout shift. */}
      <section className="relative min-h-[460px] overflow-hidden bg-card py-16 md:min-h-[520px] md:py-24">
        <div className="container">
          <div className="grid gap-10 md:grid-cols-2 md:items-center">
            <div>
              <h1 className="mb-2 text-3xl font-bold leading-tight text-primary md:text-5xl text-balance">
                {heroTitle}
              </h1>
              <p className="mb-1 text-xl font-semibold md:text-2xl">
                {heroSubtitle}
              </p>
              <p className="mb-6 text-muted-foreground text-pretty">
                {heroText}
              </p>
              <button
                onClick={() => {
                  const el = document.getElementById('instagram');
                  if (el) {
                    const y = el.getBoundingClientRect().top + window.scrollY - 80;
                    window.scrollTo({ top: y, behavior: 'smooth' });
                  }
                }}
                className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3 text-sm font-semibold text-primary-foreground transition-colors hover:bg-primary/90"
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M12 5v14"/><path d="m19 12-7 7-7-7"/></svg>
                {t('home.cta', 'Jetzt starten')}
              </button>
              <div className="mt-6 flex flex-wrap gap-x-5 gap-y-3 text-sm text-muted-foreground">
                {[
                  { slug: 'instagram', name: 'Instagram' },
                  { slug: 'tiktok', name: 'TikTok' },
                  { slug: 'youtube', name: 'Youtube' },
                  { slug: 'twitter', name: 'Twitter' },
                  { slug: 'spotify', name: 'Spotify' },
                  { slug: 'twitch', name: 'Twitch' },
                ].map(s => (
                  <span key={s.slug} className="inline-flex items-center gap-1.5">
                    <SocialIcon slug={s.slug} className="h-4 w-4" />
                    {s.name}
                  </span>
                ))}
              </div>
            </div>
            <div className="hidden md:block">
              <Suspense fallback={<div className="mx-auto h-[420px] max-w-xs rounded-2xl border bg-card shadow-lg" />}>
                <HeroNotifications />
              </Suspense>
            </div>
          </div>
        </div>
      </section>

      {/* Product Categories
          The wrapper reserves vertical space to prevent CLS while categories
          + products are being fetched. Each category section is roughly the
          same height as the skeleton block below. */}
      {loading ? (
        <div className="container py-12" style={{ contain: 'layout' }}>
          <div className="space-y-12">
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i}>
                {/* Category title placeholder – matches the real h-6 icon + h-7 title row */}
                <div className="mb-6 flex items-center gap-3">
                  <div className="h-6 w-6 animate-pulse rounded bg-muted" />
                  <div className="h-7 w-40 animate-pulse rounded bg-muted" />
                </div>
                {/* Product card grid placeholder – matches the real ProductCard height */}
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  {Array.from({ length: 5 }).map((_, j) => (
                    <div key={j} className="h-[210px] animate-pulse rounded-xl border bg-muted/40" />
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      ) : (
        categories.map((category: any) => {
          const categoryProducts = products.filter((p: any) => p.category_id === category.id)
            .filter((p: any) => {
              const title = p.title.toLowerCase();
              return !(title.startsWith('deutsche') || title.startsWith('italienische') || title.startsWith('französische') || title.startsWith('franzoesische') || title.startsWith('usa ') || title.startsWith('verifizierte'));
            });
          if (categoryProducts.length === 0) return null;

          return (
            <section key={category.id} id={category.slug} className="border-t py-12">
              <div className="container">
                <div className="mb-6 flex items-center gap-3">
                  <SocialIcon slug={category.slug} className="h-6 w-6" />
                  <h2 className="text-2xl font-bold">
                    <Link
                      to={localePath(`/${category.slug}`)}
                      className="transition-colors hover:text-primary"
                    >
                      {category.name}
                    </Link>
                  </h2>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5">
                  {categoryProducts.map((product: any) => (
                    <ProductCard key={product.id} product={product} />
                  ))}
                </div>
              </div>
            </section>
          );
        })
      )}

      {/* Trust Section */}
      <section className="border-t bg-card py-16">
        <div className="container">
          <h2 className="mb-8 text-center text-2xl font-bold">{t('home.services_title', 'Unsere Dienstleistungen')}</h2>
          <div className="grid gap-6 md:grid-cols-3">
            <div className="flex flex-col items-center rounded-xl border p-6 text-center">
              <Truck className="mb-3 h-8 w-8 text-primary" />
              <h3 className="mb-2 font-semibold">{t('home.fast_delivery', 'Rasche Lieferung')}</h3>
              <p className="text-sm text-muted-foreground">{t('home.fast_delivery_desc', 'Bestellung wird sofort abgewickelt. Resultate innerhalb von 2-48 Stunden.')}</p>
            </div>
            <div className="flex flex-col items-center rounded-xl border p-6 text-center">
              <Award className="mb-3 h-8 w-8 text-primary" />
              <h3 className="mb-2 font-semibold">{t('home.premium_quality', 'Premium Qualität')}</h3>
              <p className="text-sm text-muted-foreground">{t('home.premium_quality_desc', 'Wir setzen auf höchste Kundenzufriedenheit und garantieren die beste Qualität.')}</p>
            </div>
            <div className="flex flex-col items-center rounded-xl border p-6 text-center">
              <Shield className="mb-3 h-8 w-8 text-primary" />
              <h3 className="mb-2 font-semibold">{t('home.secure_payment', 'Sichere Zahlung')}</h3>
              <p className="text-sm text-muted-foreground">{t('home.secure_payment_desc', 'Zahlung durch Twint, Visa oder Mastercard durch SSL Verschlüsselung geschützt.')}</p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ / About */}
      <section className="border-t py-16">
        <div className="container max-w-3xl">
          <h2 className="mb-4 text-2xl font-bold">{t('home.faq1_q', 'Warum sollte ich Followers kaufen?')}</h2>
          <p className="mb-8 text-muted-foreground text-pretty">
            {t('home.faq1_a', 'Je mehr Follower du hast, desto mehr Aufmerksamkeit wirst du von anderen Nutzern oder Unternehmen bekommen. Gute Inhalte sind der Schlüssel, um die Followerzahlen zu steigern.')}
          </p>
          <h2 className="mb-4 text-2xl font-bold">{t('home.faq2_q', 'Sind die Followers echt?')}</h2>
          <p className="text-muted-foreground text-pretty">
            {t('home.faq2_a', 'Ja, wir liefern ausschliesslich echte Follower von echten und aktiven Accounts. Alle diese Nutzer haben Profilbilder, Posts, Follower und folgen auch anderen Accounts.')}
          </p>
        </div>
      </section>
    </>
  );
}
